import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Select,
  Switch,
  Text,
  Spinner,
  FormHelperText,
  Heading,
  Divider,
  Grid,
  GridItem,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  FormErrorMessage,
  Checkbox,
} from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { FaPlay, FaEnvelope, FaSms, FaWhatsapp } from "react-icons/fa";
import { componentState } from "../../../atoms/componentState";
import TitleFlex from "./components/titleFlex";
import { NumericFormat, PatternFormat } from "react-number-format";
import { stateOptions } from "../../../constants/stateOptions";
import moment from "moment";
import "moment/locale/pt-br";
import { dateForAPI, dateForValueInput } from "../../../constants/dataFormat";
import { cnpj as cnpjValidator, cpf as cpfValidator } from "cpf-cnpj-validator";
import { JWTToken } from "../../../routes/privateRoute";
import jwt_decode from "jwt-decode";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { goToVendaBolePixListagem } from "../../../routes/coordinator";
import { customColors } from "../../../atoms/customColors";
import { useSetRecoilState, useRecoilState } from "recoil";
import ModalRevisãoVendaBoletoLote from "./components/ModalRevisãoVendaBoletoLote";
import VendasBoletoLoteApi from "../../../services/VendasBoletoloteApi";
import axios from "axios";

/**
 * Extrai a mensagem de erro mais útil de uma falha de request, cobrindo os
 * formatos que a cadeia web/core pode retornar:
 *  - objeto { _error: "..." }         (erro de negócio do core)
 *  - objeto { message: "..." }
 *  - array  ["campo: msg", ...]        (validação de ModelState)
 *  - string crua
 * Cai para error.message em vez de despejar o objeto inteiro na tela.
 */
export function extrairMensagemErro(error: any): string {
  const data = error?.response?.data;
  if (data) {
    if (typeof data === "string" && data.trim()) return data;
    if (data._error) return data._error;
    if (data.message) return data.message;
    if (Array.isArray(data) && data.length) return data.join(" | ");
  }
  return error?.message || "Erro inesperado. Tente novamente.";
}

export interface VendaBoleto {
  idCliente: number;
  valor: number;
  valorPrimeiraEmissao: number;
  vencimento: string;
  diaVencimento: number;
  descricao: string;
  cpfPagador: string;
  nomePagador: string;
  emailPagador: string;
  enderRua: string;
  enderBairro: string;
  enderCEP: string;
  enderCidade: string;
  enderComplemento: string;
  enderEstado: string;
  enderNumero: string;
  enderReferencia: string;
  permitirRegras: boolean;
  isCancelado: boolean;
  mesesPular: number;
  dataPrimeiraEmissao: string;
  numeroPagador: string;
  isAvulso: boolean;
  dataEmissao?: string;
  enviarEmail: boolean;
  enviarSMS: boolean;
  enviarWhats: boolean;
  RegrasJuros: {
    mode: string;
    frequency: string | null;
    value: number;
    start_at: string;
  } | null;
  RegrasMulta: {
    mode: string;
    value: number;
    start_at: string;
  };
  RegrasDesconto: {
    mode: string;
    value: number;
    diasAntesVencimento: number;
  };
  descontoNaPrimeiraParcela: boolean;
  dataLimitePagamento?: string;
}

function toCentsExact(value: any) {
  if (value == null) throw new Error("Input value cannot be null or undefined");
  let valueStr = value.toString().replace(",", ".");
  const num = parseFloat(valueStr);
  if (isNaN(num)) throw new Error("Invalid number format");
  return Math.round(num * 100);
}

const getInterestConfig = (currentMode: string) => {
  switch (currentMode) {
    case "VALOR_DIARIO":
      return { mode: "FIXED", frequency: "DAILY", isPercentage: false };
    case "PERCENTUAL_DIARIO":
      return { mode: "PERCENTAGE", frequency: "DAILY", isPercentage: true };
    case "PERCENTUAL_MENSAL":
      return { mode: "PERCENTAGE", frequency: "MONTHLY", isPercentage: true };
    case "SEM_JUROS":
    default:
      return { mode: "SEM_JUROS", frequency: null, isPercentage: false };
  }
};

const getMultaConfig = (currentMode: string) => {
  switch (currentMode) {
    case "VALOR_FIXO":
      return { mode: "FIXED", isPercentage: false };
    case "PERCENTUAL":
      return { mode: "PERCENTAGE", isPercentage: true };
    case "SEM_MULTA":
    default:
      return { mode: "SEM_MULTA", isPercentage: false };
  }
};

const convertValueToCentsString = (
  value: number,
  isPercentage: boolean
): string => {
  if (isPercentage) {
    return Math.round(value * 100).toString();
  } else {
    return toCentsExact(value).toString();
  }
};

export default function VendasBolePixLote() {
  const setHeaderLocation = useSetRecoilState(componentState);
  useEffect(() => setHeaderLocation("Venda no Boleto em Lote"), []);
  const navigate = useNavigate();
  const colors = useRecoilState(customColors)[0];
  const token = window.localStorage.getItem("token");
  let jwtToken: JWTToken = jwt_decode(token || "");
  const cliente = Number(jwtToken.idCliente);
  const idLogin = Number(jwtToken.primarysid);
  const unMask = (value: any) => value.replace(/[^0-9]/g, "");

  const initialValues: VendaBoleto = {
    idCliente: cliente,
    valor: 0,
    valorPrimeiraEmissao: 0,
    vencimento: moment().format(dateForAPI),
    diaVencimento: 1,
    descricao: "",
    cpfPagador: "",
    nomePagador: "",
    emailPagador: "",
    enderRua: "",
    enderBairro: "",
    enderCEP: "",
    enderCidade: "",
    enderComplemento: "",
    enderEstado: "AC",
    enderNumero: "",
    enderReferencia: "",
    permitirRegras: false,
    isCancelado: false,
    mesesPular: 0,
    dataPrimeiraEmissao: moment().format(dateForAPI),
    numeroPagador: "",
    isAvulso: false,
    dataEmissao: "",
    enviarEmail: true,
    enviarSMS: false,
    enviarWhats: false,
    RegrasJuros: { mode: "SEM_JUROS", frequency: null, value: 0, start_at: "" },
    RegrasMulta: { mode: "SEM_MULTA", value: 0, start_at: "" },
    RegrasDesconto: { mode: "SEM_DESCONTO", value: 0, diasAntesVencimento: 0 },
    descontoNaPrimeiraParcela: false,
  };

  const [boleto, setBoleto] = useState<VendaBoleto>(initialValues);
  const [numBoletos, setNumBoletos] = useState<number>(2);
  const [batchResponses, setBatchResponses] = useState<any[]>([]);
  const [batchObjects, setBatchObjects] = useState<VendaBoleto[]>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [loadingState, setLoadingState] = useState<boolean>(false);
  const [validation, setValidation] = useState<boolean>(false);
  const [sameMonth, setSameMonth] = useState<boolean>(false);
  const [monthsToSkip, setMonthsToSkip] = useState<number>(0);

  function onChangeValorFixo(e: any) {
    setBoleto({
      ...boleto,
      valor: Number(e.target.value.slice(3).replace(",", ".")),
    });
  }

  function onChangeValorPrimeiraEmissao(e: any) {
    setBoleto({
      ...boleto,
      valorPrimeiraEmissao: Number(e.target.value.slice(3).replace(",", ".")),
    });
  }

  useEffect(() => {
    setBoleto({ ...boleto, mesesPular: sameMonth ? -1 : monthsToSkip });
  }, [sameMonth, monthsToSkip]);

  async function handleBatchCreation() {
    setLoadingState(true);
    try {
      const interestConfig = getInterestConfig(
        boleto.RegrasJuros?.mode || "SEM_JUROS"
      );
      const multaConfig = getMultaConfig(boleto.RegrasMulta.mode);
      const lotePayload = {
        idCliente: cliente,
        idLogin: idLogin,
        diaVencimento: boleto.diaVencimento,
        parcelas: numBoletos,
        isCancelado: false,
        dataPrimeiraEmissao: moment(boleto.dataPrimeiraEmissao).format(
          dateForAPI
        ),
        mensagem: boleto.descricao,
        qtdMesesPular: boleto.mesesPular,
        descontoNaPrimeiraParcela: boleto.descontoNaPrimeiraParcela,
        numeroPagador: unMask(boleto.numeroPagador),
        valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
        enviarEmail: boleto.enviarEmail,
        enviarSMS: boleto.enviarSMS,
        enviarWhats: boleto.enviarWhats,
        boletoBase: {
          idLogin: idLogin,
          idCliente: cliente,
          dataVencimento: moment(boleto.dataPrimeiraEmissao)
            .date(boleto.diaVencimento)
            .format(dateForAPI),
          dataEmissao: moment().format(dateForAPI),
          isAvulso: false,
          valor: Number(boleto.valor).toFixed(2),
          valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
          mensagem: boleto.descricao,
          cpfPagador: unMask(boleto.cpfPagador),
          nomePagador: boleto.nomePagador,
          emailPagador: boleto.emailPagador,
          enderRua: boleto.enderRua,
          enderBairro: boleto.enderBairro,
          enderCEP: unMask(boleto.enderCEP),
          enderCidade: boleto.enderCidade,
          enderComplemento: boleto.enderComplemento,
          enderEstado: boleto.enderEstado,
          enderNumero: boleto.enderNumero,
          enderReferencia: boleto.enderReferencia,
          permitirRegras: boleto.permitirRegras,
          enviarEmail: boleto.enviarEmail,
          enviarSMS: boleto.enviarSMS,
          enviarWhats: boleto.enviarWhats,
          RegrasJuros: {
            mode:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.mode
                : "SEM_JUROS",
            frequency:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.frequency
                : null,
            value:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? convertValueToCentsString(
                  boleto.RegrasJuros?.value || 0,
                  interestConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasMulta: {
            mode:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? multaConfig.mode
                : "SEM_MULTA",
            value:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? convertValueToCentsString(
                  boleto.RegrasMulta.value,
                  multaConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasDesconto: {
            mode:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? boleto.RegrasDesconto.mode
                : "SEM_DESCONTO",
            value:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? convertValueToCentsString(boleto.RegrasDesconto.value, false)
                : "0",
            diasAntesVencimento:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? Number(boleto.RegrasDesconto.diasAntesVencimento)
                : 0,
          },
        },
      };
      const response = await VendasBoletoLoteApi.postVendaBoletoLote(
        lotePayload
      );
      setBatchResponses(response.boletos);
      toast.success("Lote de boletos criado com sucesso!");
      return response;
    } catch (error: any) {
      toast.error(`Erro ao criar lote: ${extrairMensagemErro(error)}`);
      return undefined;
    } finally {
      setLoadingState(false);
    }
  }

  async function onSubmit() {
    const valorMinimo = boleto.permitirRegras ? 10 : 5;
    if (
      boleto.valor < valorMinimo ||
      boleto.valorPrimeiraEmissao < valorMinimo
    ) {
      toast.error(
        "Os valores dos boletos devem ser maiores ou iguais ao mínimo!"
      );
      return;
    }

    const cpfLength = unMask(boleto.cpfPagador).length;
    if (cpfLength < 11 || cpfLength > 14) {
      toast.error("O CPF/CNPJ deve ter entre 11 e 14 dígitos.");
      return;
    }
    if (unMask(boleto.enderCEP).length !== 8) {
      toast.error("O CEP deve ter exatamente 8 dígitos.");
      return;
    }
    if (unMask(boleto.numeroPagador).length < 10) {
      toast.error("O número de telefone deve ter pelo menos 10 dígitos.");
      return;
    }
    if (numBoletos < 2 || numBoletos > 99999) {
      toast.error("O número de boletos deve ser entre 2 e 99999.");
      return;
    }
    if (
      moment(boleto.dataPrimeiraEmissao).isSame(moment(), "day") &&
      Number(moment().format("HH.mm")) > 17
    ) {
      toast.error(
        "Não é possível realizar solicitações para o mesmo dia após as 16hs!"
      );
      return;
    }
    if (boleto.mesesPular < -1) {
      toast.error("O número de meses a pular não pode ser menor que -1.");
      return;
    }
    if (boleto.diaVencimento < 1 || boleto.diaVencimento > 31) {
      toast.error("O dia de vencimento deve estar entre 1 e 31.");
      return;
    }

    const erroRegra =
      getRegraJurosErro() || getRegraMultaErro() || getRegraDescontoErro();
    if (erroRegra) {
      toast.error(erroRegra);
      return;
    }

    const erroDiaVenc = getDiaVencimentoErro();
    if (erroDiaVenc) {
      toast.error(erroDiaVenc);
      return;
    }

    setLoadingState(true);
    try {
      const interestConfig = getInterestConfig(
        boleto.RegrasJuros?.mode || "SEM_JUROS"
      );
      const multaConfig = getMultaConfig(boleto.RegrasMulta.mode);
      const lotePayload = {
        idCliente: cliente,
        idLogin: idLogin,
        diaVencimento: boleto.diaVencimento,
        parcelas: numBoletos,
        isCancelado: false,
        dataPrimeiraEmissao: moment(boleto.dataPrimeiraEmissao).format(
          dateForAPI
        ),
        mensagem: boleto.descricao,
        qtdMesesPular: boleto.mesesPular,
        descontoNaPrimeiraParcela: boleto.descontoNaPrimeiraParcela,
        numeroPagador: unMask(boleto.numeroPagador),
        valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
        enviarEmail: boleto.enviarEmail,
        enviarSMS: boleto.enviarSMS,
        enviarWhats: boleto.enviarWhats,
        boletoBase: {
          idLogin: idLogin,
          idCliente: cliente,
          dataVencimento: moment(boleto.dataPrimeiraEmissao)
            .date(boleto.diaVencimento)
            .format(dateForAPI),
          dataEmissao: moment().format(dateForAPI),
          isAvulso: false,
          valor: Number(boleto.valor).toFixed(2),
          valorPrimeiraEmissao: Number(boleto.valorPrimeiraEmissao).toFixed(2),
          mensagem: boleto.descricao,
          cpfPagador: unMask(boleto.cpfPagador),
          nomePagador: boleto.nomePagador,
          emailPagador: boleto.emailPagador,
          enderRua: boleto.enderRua,
          enderBairro: boleto.enderBairro,
          enderCEP: unMask(boleto.enderCEP),
          enderCidade: boleto.enderCidade,
          enderComplemento: boleto.enderComplemento,
          enderEstado: boleto.enderEstado,
          enderNumero: boleto.enderNumero,
          enderReferencia: boleto.enderReferencia,
          permitirRegras: boleto.permitirRegras,
          enviarEmail: boleto.enviarEmail,
          enviarSMS: boleto.enviarSMS,
          enviarWhats: boleto.enviarWhats,
          RegrasJuros: {
            mode:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.mode
                : "SEM_JUROS",
            frequency:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? interestConfig.frequency
                : null,
            value:
              boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                ? convertValueToCentsString(
                  boleto.RegrasJuros?.value || 0,
                  interestConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasMulta: {
            mode:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? multaConfig.mode
                : "SEM_MULTA",
            value:
              boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                ? convertValueToCentsString(
                  boleto.RegrasMulta.value,
                  multaConfig.isPercentage
                )
                : "0",
            start_at: moment(boleto.dataPrimeiraEmissao)
              .date(boleto.diaVencimento)
              .format(dateForAPI),
          },
          RegrasDesconto: {
            mode:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? boleto.RegrasDesconto.mode
                : "SEM_DESCONTO",
            value:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? convertValueToCentsString(boleto.RegrasDesconto.value, false)
                : "0",
            diasAntesVencimento:
              boleto.permitirRegras &&
                boleto.RegrasDesconto.mode !== "SEM_DESCONTO"
                ? Number(boleto.RegrasDesconto.diasAntesVencimento)
                : 0,
          },
        },
      };
      const response =
        await VendasBoletoLoteApi.postVendaBoletoLoteVisualizacao(lotePayload);
      const previewObjects: VendaBoleto[] = response.boletos.map(
        (item: any, index: number) => {
          const interestConfig = getInterestConfig(
            boleto.RegrasJuros?.mode || "SEM_JUROS"
          );
          const multaConfig = getMultaConfig(boleto.RegrasMulta.mode);
          return {
            ...boleto,
            valor: index === 0 ? boleto.valorPrimeiraEmissao : boleto.valor,
            vencimento: item.dataVencimento,
            dataLimitePagamento: item.dataLimitePagamento,
            dataPrimeiraEmissao:
              item.dataPrimeiraEmissao || boleto.dataPrimeiraEmissao,
            descricao: `${boleto.descricao} - Parcela ${index + 1}`,
            cpfPagador: unMask(boleto.cpfPagador),
            enderCEP: unMask(boleto.enderCEP),
            numeroPagador: unMask(boleto.numeroPagador),
            enviarEmail: boleto.enviarEmail,
            enviarSMS: boleto.enviarSMS,
            enviarWhats: boleto.enviarWhats,
            RegrasJuros: {
              mode:
                boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                  ? interestConfig.mode
                  : "SEM_JUROS",
              frequency:
                boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                  ? interestConfig.frequency
                  : null,
              value:
                boleto.permitirRegras && interestConfig.mode !== "SEM_JUROS"
                  ? convertValueToCentsString(
                    boleto.RegrasJuros?.value || 0,
                    interestConfig.isPercentage
                  )
                  : "0",
              start_at: item.dataVencimento,
            },
            RegrasMulta: {
              mode:
                boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                  ? multaConfig.mode
                  : "SEM_MULTA",
              value:
                boleto.permitirRegras && multaConfig.mode !== "SEM_MULTA"
                  ? convertValueToCentsString(
                    boleto.RegrasMulta.value,
                    multaConfig.isPercentage
                  )
                  : "0",
              start_at: item.dataVencimento,
            },
            RegrasDesconto: {
              mode:
                boleto.permitirRegras &&
                  boleto.RegrasDesconto.mode !== "SEM_DESCONTO" &&
                  (index !== 0 || boleto.descontoNaPrimeiraParcela)
                  ? boleto.RegrasDesconto.mode
                  : "SEM_DESCONTO",
              value:
                boleto.permitirRegras &&
                  boleto.RegrasDesconto.mode !== "SEM_DESCONTO" &&
                  (index !== 0 || boleto.descontoNaPrimeiraParcela)
                  ? convertValueToCentsString(boleto.RegrasDesconto.value, false)
                  : "0",
              diasAntesVencimento:
                boleto.permitirRegras &&
                  boleto.RegrasDesconto.mode !== "SEM_DESCONTO" &&
                  (index !== 0 || boleto.descontoNaPrimeiraParcela)
                  ? Number(boleto.RegrasDesconto.diasAntesVencimento)
                  : 0,
            },
          };
        }
      );
      setBatchObjects(previewObjects);
      setIsOpen(true);
      // Modal aberta assume o controle; libera o botão da página.
      setLoadingState(false);
    } catch (error: any) {
      toast.error(`Erro ao obter visualização: ${extrairMensagemErro(error)}`);
      setLoadingState(false);
    }
  }

  function validateCPForCNPJ(item: string) {
    const temp = unMask(item);
    if (temp.length < 11 || temp.length > 14) {
      setValidation(true);
      return;
    }
    setValidation(
      temp.length === 11
        ? !cpfValidator.isValid(temp)
        : !cnpjValidator.isValid(temp)
    );
  }

  async function addressCep(cep: any) {
    try {
      if (unMask(cep).length >= 8) {
        const { data } = await axios.get(
          `https://viacep.com.br/ws/${cep}/json/`
        );
        if (data.erro) {
          toast.error("CEP não encontrado!");
          return;
        }
        setBoleto((prev) => ({
          ...prev,
          enderRua: data.logradouro,
          enderBairro: data.bairro,
          enderCEP: data.cep,
          enderCidade: data.localidade,
          enderComplemento: data.complemento,
          enderEstado: data.uf,
        }));
      }
    } catch (error) {
      console.log(error);
    }
  }

  function validarMesmoDiaEAntesDasDezesseisHoras(data: any) {
    const horaDaSolicitacao = moment().format("HH.mm");
    const diadaSolicitação =
      moment().format("DD-MM-YYYY") === moment(data).format("DD-MM-YYYY");
    return !diadaSolicitação || Number(horaDaSolicitacao) < 16;
  }

  const validarData = (e: any) => {
    if (validarMesmoDiaEAntesDasDezesseisHoras(e.target.value)) {
      setBoleto({
        ...boleto,
        dataPrimeiraEmissao: moment(e.target.value).format(dateForAPI),
      });
    } else {
      toast.error(
        "Não é possível realizar solicitações para o mesmo dia após as 16hs!"
      );
    }
  };

  function validaData(item: any) {
    if (moment(item) < moment().startOf("day"))
      toast.error("A data não pode ser anterior ao dia de hoje!");
  }

  const OPTIONS_INTEREST = [
    { key: 1, value: "SEM_JUROS", text: "Sem juros" },
    { key: 2, value: "VALOR_DIARIO", text: "Valor diário" },
    { key: 3, value: "PERCENTUAL_DIARIO", text: "Percentual diário" },
    { key: 4, value: "PERCENTUAL_MENSAL", text: "Percentual mensal" },
  ];

  const OPTIONS_MULTA = [
    { key: 1, value: "SEM_MULTA", text: "Sem Multa" },
    { key: 2, value: "VALOR_FIXO", text: "Valor Fixo" },
    { key: 3, value: "PERCENTUAL", text: "Valor em Porcentagem" },
  ];

  const OPTIONS_DESCONTO = [
    { key: 1, value: "SEM_DESCONTO", text: "Sem Desconto" },
    { key: 2, value: "FIXED", text: "Valor Fixo" },
    // Percentual preparado no back/validação, mas oculto no front por ora:
    // { key: 3, value: "PERCENTAGE", text: "Percentual" },
  ];

  // Limites legais (BACEN) — % real (o ×100 só ocorre no build do payload)
  const LIMITE_MULTA_PCT = 2;
  const LIMITE_JUROS_MENSAL_PCT = 1;
  const LIMITE_JUROS_DIARIO_PCT = 1 / 30;

  const getRegraJurosErro = (): string | null => {
    const rj = boleto.RegrasJuros;
    if (!boleto.permitirRegras || !rj || rj.mode === "SEM_JUROS") return null;
    const v = Number(rj.value) || 0;
    if (v <= 0) return "Informe o valor do juros.";
    if (rj.mode === "PERCENTUAL_MENSAL" && v > LIMITE_JUROS_MENSAL_PCT)
      return "Juros mensal não pode exceder 1% (limite legal).";
    if (rj.mode === "PERCENTUAL_DIARIO" && v > LIMITE_JUROS_DIARIO_PCT)
      return "Juros diário não pode exceder 0,033% (equivale a 1% ao mês).";
    if (rj.mode === "VALOR_DIARIO") {
      // Sobre o valor da mensalidade (não a entrada); piso R$ 0,01/dia (BACEN)
      const tetoJurosDia = Math.max(
        boleto.valor * (LIMITE_JUROS_DIARIO_PCT / 100),
        0.01
      );
      if (v > tetoJurosDia)
        return `Juros diário fixo não pode exceder 0,033%/dia da mensalidade (máx. R$ ${tetoJurosDia
          .toFixed(2)
          .replace(".", ",")}/dia).`;
    }
    return null;
  };

  const getRegraMultaErro = (): string | null => {
    const rm = boleto.RegrasMulta;
    if (!boleto.permitirRegras || !rm || rm.mode === "SEM_MULTA") return null;
    const v = Number(rm.value) || 0;
    if (v <= 0) return "Informe o valor da multa.";
    if (rm.mode === "PERCENTUAL" && v > LIMITE_MULTA_PCT)
      return "Multa não pode exceder 2% (limite legal).";
    if (rm.mode === "VALOR_FIXO") {
      // Sobre o valor da mensalidade (não a entrada)
      const tetoMulta = boleto.valor * (LIMITE_MULTA_PCT / 100);
      if (v > tetoMulta)
        return `Multa fixa não pode exceder 2% da mensalidade (máx. R$ ${tetoMulta
          .toFixed(2)
          .replace(".", ",")}).`;
    }
    return null;
  };

  const getRegraDescontoErro = (): string | null => {
    const rd = boleto.RegrasDesconto;
    if (!boleto.permitirRegras || !rd || rd.mode === "SEM_DESCONTO") return null;
    const v = Number(rd.value) || 0;
    if (v <= 0) return "Informe o valor do desconto.";
    if (rd.mode === "FIXED" && v >= boleto.valor)
      return "Desconto não pode ser igual ou maior que o valor da parcela.";
    if (Number(rd.diasAntesVencimento) < 0)
      return "Os dias de antecipação não podem ser negativos.";
    return null;
  };

  // Tetos calculados sobre o valor da mensalidade (não a entrada)
  const fmtBRL = (n: number) =>
    n.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });
  const tetoMultaFixa = boleto.valor * (LIMITE_MULTA_PCT / 100);
  const tetoJurosDiaFixo = Math.max(
    boleto.valor * (LIMITE_JUROS_DIARIO_PCT / 100),
    0.01
  );

  // Mesmo mês: o dia de vencimento da 2ª parcela deve ser após o dia da 1ª emissão
  const getDiaVencimentoErro = (): string | null => {
    if (!sameMonth) return null;
    const diaPrimeira = moment(boleto.dataPrimeiraEmissao).date();
    if (boleto.diaVencimento <= diaPrimeira)
      return `No mesmo mês, o dia de vencimento (${boleto.diaVencimento}) deve ser maior que o dia da 1ª emissão (${diaPrimeira}).`;
    return null;
  };

  const isFormValid = () => {
    const cpfLength = unMask(boleto.cpfPagador).length;
    const cepLength = unMask(boleto.enderCEP).length;
    const numeroPagadorLength = unMask(boleto.numeroPagador).length;
    const hasNotification =
      boleto.enviarEmail || boleto.enviarSMS || boleto.enviarWhats;
    return (
      cliente > 0 &&
      boleto.valor > 0.01 &&
      boleto.valorPrimeiraEmissao > 0.01 &&
      cpfLength >= 11 &&
      cpfLength <= 14 &&
      boleto.emailPagador !== "" &&
      boleto.nomePagador !== "" &&
      cepLength === 8 &&
      boleto.enderRua !== "" &&
      boleto.enderNumero !== "" &&
      boleto.enderBairro !== "" &&
      boleto.enderCidade !== "" &&
      boleto.enderEstado !== "" &&
      numeroPagadorLength >= 10 &&
      numBoletos >= 2 &&
      numBoletos <= 99999 &&
      boleto.mesesPular >= -1 &&
      boleto.diaVencimento >= 1 &&
      boleto.diaVencimento <= 31 &&
      hasNotification &&
      !getRegraJurosErro() &&
      !getRegraMultaErro() &&
      !getRegraDescontoErro() &&
      !getDiaVencimentoErro()
    );
  };

  // Exemplo dinâmico do intervalo até a 2ª parcela
  const capitalizarMes = (s: string) =>
    s.charAt(0).toUpperCase() + s.slice(1);
  const mesPrimeiraLabel = capitalizarMes(
    moment(boleto.dataPrimeiraEmissao).locale("pt-br").format("MMMM [de] YYYY")
  );
  const mesSegundaLabel = capitalizarMes(
    moment(boleto.dataPrimeiraEmissao)
      .locale("pt-br")
      .add(monthsToSkip + 1, "months")
      .format("MMMM [de] YYYY")
  );

  return (
    <Flex
      w={"100%"}
      h={"90vh"}
      bgColor={colors.WHITE}
      p={{ base: 4, md: 8 }}
      shadow="lg"
      overflowY="auto"
      flexDir="column"
      maxW="1400px"
      mx="auto"
    >
      <Box mb={6}>
        <Flex alignItems="center" mb={4}>
          <FaPlay color={colors.BLUEMONEDDBUTTONS} size="28px" />
          <Heading as="h1" size="lg" ml={3} color={colors.BLUEMONEDDBUTTONS}>
            Criar BOLEPIX em LOTE
          </Heading>
        </Flex>
        <Divider borderColor="gray.200" />
      </Box>

      <Box mb={8}>
        <TitleFlex item="Informações de Pagamento" />
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(1, 1fr)" }}
          gap={4}
          my={4}
        >
          <GridItem>
            <FormControl isRequired>
              <FormLabel>
                Valor Primeira Parcela (mínimo R$
                {boleto.permitirRegras ? "10,00" : "5,00"})
              </FormLabel>
              <NumericFormat
                decimalScale={2}
                prefix="R$ "
                value={boleto.valorPrimeiraEmissao}
                decimalSeparator=","
                customInput={Input}
                placeholder="R$ 0,00"
                onChange={onChangeValorPrimeiraEmissao}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>
                Valor das Demais Parcelas (mínimo R$
                {boleto.permitirRegras ? "10,00" : "5,00"})
              </FormLabel>
              <NumericFormat
                decimalScale={2}
                prefix="R$ "
                value={boleto.valor}
                decimalSeparator=","
                customInput={Input}
                placeholder="R$ 0,00"
                onChange={onChangeValorFixo}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Data de Vencimento do Primeiro Bolepix</FormLabel>
              <Input
                type="date"
                value={moment(boleto.dataPrimeiraEmissao).format(
                  dateForValueInput
                )}
                min={moment().startOf("day").format(dateForValueInput)}
                onChange={validarData}
                onBlur={(e) => validaData(e.target.value)}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Dia de Vencimento (1 a 31)</FormLabel>
              <NumberInput
                value={boleto.diaVencimento}
                min={1}
                max={31}
                onChange={(v) =>
                  setBoleto({ ...boleto, diaVencimento: parseInt(v) })
                }
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
              <FormHelperText>
                Cada boleto subsequente terá vencimento neste dia do mês,
                ajustado para o último dia válido se necessário.
              </FormHelperText>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Quantidade de Parcelas</FormLabel>
              <NumberInput
                value={numBoletos}
                min={2}
                max={99999}
                onChange={(v) => setNumBoletos(parseInt(v))}
              >
                <NumberInputField />
                <NumberInputStepper>
                  <NumberIncrementStepper />
                  <NumberDecrementStepper />
                </NumberInputStepper>
              </NumberInput>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>Descrição Base</FormLabel>
              <Input
                placeholder="Opcional (ex: Mensalidade Semestre 1)"
                value={boleto.descricao}
                onChange={(e) =>
                  setBoleto({ ...boleto, descricao: e.target.value })
                }
              />
              <FormHelperText>
                Cada boleto terá " - Parcela X" anexado à descrição.
              </FormHelperText>
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>
                Você deseja emitir a segunda parcela no mesmo mês de vencimento
                da primeira?
              </FormLabel>
              <Switch
                isChecked={sameMonth}
                onChange={(e) => setSameMonth(e.target.checked)}
              />
              <FormHelperText>
                Se sim, o dia de vencimento deve ser após o dia da primeira
                emissão.
              </FormHelperText>
              {sameMonth &&
                (getDiaVencimentoErro() ? (
                  <Text
                    mt={2}
                    fontSize="sm"
                    color="red.500"
                    fontWeight="medium"
                  >
                    {getDiaVencimentoErro()}
                  </Text>
                ) : (
                  <Text
                    mt={2}
                    fontSize="sm"
                    color="green.500"
                    fontWeight="medium"
                  >
                    OK — 2ª parcela em{" "}
                    {moment(boleto.dataPrimeiraEmissao)
                      .date(boleto.diaVencimento)
                      .format("DD/MM/YYYY")}
                    .
                  </Text>
                ))}
              {!sameMonth && <Divider my={4} borderColor="gray.200" />}
            </FormControl>
            {!sameMonth && (
              <FormControl mt={4} isRequired>
                <FormLabel>Intervalo até a 2ª parcela</FormLabel>
                <Alert
                  status="info"
                  variant="left-accent"
                  borderRadius="md"
                  mb={3}
                  alignItems="flex-start"
                >
                  <AlertIcon />
                  <Box fontSize="sm">
                    <Text>
                      Quantos meses <b>pular</b> entre a 1ª emissão e a 2ª
                      parcela.
                    </Text>
                    <Text color="gray.600">
                      <b>0</b> = mês seguinte &nbsp;·&nbsp; <b>1</b> = pula 1 mês
                      &nbsp;·&nbsp; <b>2</b> = pula 2 meses…
                    </Text>
                  </Box>
                </Alert>
                <NumberInput
                  value={monthsToSkip}
                  min={0}
                  max={99999}
                  onChange={(v) => setMonthsToSkip(parseInt(v) || 0)}
                >
                  <NumberInputField />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
                <Text
                  mt={2}
                  fontSize="sm"
                  fontWeight="semibold"
                  color={colors.BLUEMONEDDBUTTONS}
                >
                  Exemplo: 1ª em {mesPrimeiraLabel} → 2ª em {mesSegundaLabel}.
                </Text>
              </FormControl>
            )}
          </GridItem>
        </Grid>
      </Box>

      <Box mb={8}>
        <Flex alignItems="center" mb={4}>
          <Switch
            size="md"
            mr={3}
            isChecked={boleto.permitirRegras}
            onChange={() =>
              setBoleto({
                ...boleto,
                permitirRegras: !boleto.permitirRegras,
                RegrasJuros: {
                  mode: "SEM_JUROS",
                  frequency: null,
                  value: 0,
                  start_at: boleto.vencimento,
                },
                RegrasMulta: {
                  mode: "SEM_MULTA",
                  value: 0,
                  start_at: boleto.vencimento,
                },
                RegrasDesconto: {
                  mode: "SEM_DESCONTO",
                  value: 0,
                  diasAntesVencimento: 0,
                },
                descontoNaPrimeiraParcela: false,
              })
            }
          />
          <Text fontWeight="medium">Aplicar Regras de Juros, Multa e Desconto</Text>
        </Flex>
        {boleto.permitirRegras && (
          <>
            <TitleFlex item="Regras do Boleto" />

            {/* Bloco 1 — Juros e Multa */}
            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
              mb={4}
            >
              <Text fontWeight="bold" fontSize="md" mb={2}>
                Juros e Multa
              </Text>
              <Alert
                status="info"
                variant="left-accent"
                borderRadius="md"
                mb={4}
                alignItems="flex-start"
              >
                <AlertIcon />
                <Box fontSize="sm">
                  <Text>
                    <b>Multa</b> — única, aplicada após o vencimento: valor fixo
                    em R$ ou percentual (<b>máx. 2%</b>).
                  </Text>
                  <Text>
                    <b>Juros</b> — por dia de atraso a partir do vencimento de
                    cada parcela: valor fixo diário em R$, ou percentual{" "}
                    <b>diário (máx. 0,033%)</b> ou <b>mensal (máx. 1%)</b>.
                  </Text>
                  <Text color="gray.600">
                    Para boletos abaixo de R$ 100,00, o juros mínimo é 1 centavo
                    por dia.
                  </Text>
                </Box>
              </Alert>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
              <GridItem>
                <FormControl>
                  <FormLabel>Regras de Juros</FormLabel>
                  <Select
                    value={boleto.RegrasJuros?.mode || "SEM_JUROS"}
                    onChange={(e) => {
                      const config = getInterestConfig(e.target.value);
                      setBoleto({
                        ...boleto,
                        RegrasJuros: {
                          mode: e.target.value,
                          frequency: config.frequency,
                          value: 0,
                          start_at: boleto.vencimento,
                        },
                      });
                    }}
                  >
                    {OPTIONS_INTEREST.map((item) => (
                      <option key={item.key} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {boleto.RegrasJuros?.mode === "PERCENTUAL_DIARIO" && (
                  <FormControl mt={4} isInvalid={!!getRegraJurosErro()}>
                    <FormLabel>Percentual Diário (%)</FormLabel>
                    <NumericFormat
                      decimalSeparator=","
                      decimalScale={3}
                      customInput={Input}
                      value={boleto.RegrasJuros.value}
                      placeholder="0,000"
                      onValueChange={({ value }) => {
                        const num = parseFloat(value.replace(",", ".")) || 0;
                        setBoleto({
                          ...boleto,
                          RegrasJuros: { ...boleto.RegrasJuros!, value: num },
                        });
                      }}
                    />
                    <FormErrorMessage>{getRegraJurosErro()}</FormErrorMessage>
                    <FormHelperText>Máx. 0,033% ao dia.</FormHelperText>
                  </FormControl>
                )}

                {boleto.RegrasJuros?.mode === "PERCENTUAL_MENSAL" && (
                  <FormControl mt={4} isInvalid={!!getRegraJurosErro()}>
                    <FormLabel>Percentual Mensal (%)</FormLabel>
                    <NumericFormat
                      decimalSeparator=","
                      decimalScale={2}
                      customInput={Input}
                      value={boleto.RegrasJuros.value}
                      placeholder="0,00"
                      onValueChange={({ value }) => {
                        const num = parseFloat(value.replace(",", ".")) || 0;
                        setBoleto({
                          ...boleto,
                          RegrasJuros: { ...boleto.RegrasJuros!, value: num },
                        });
                      }}
                    />
                    <FormErrorMessage>{getRegraJurosErro()}</FormErrorMessage>
                    <FormHelperText>Máx. 1% ao mês.</FormHelperText>
                  </FormControl>
                )}

                {boleto.RegrasJuros?.mode === "VALOR_DIARIO" && (
                  <FormControl mt={4} isInvalid={!!getRegraJurosErro()}>
                    <FormLabel>Valor Diário (R$)</FormLabel>
                    <NumericFormat
                      decimalSeparator=","
                      decimalScale={2}
                      customInput={Input}
                      value={boleto.RegrasJuros.value}
                      placeholder="0,00"
                      onValueChange={({ value }) => {
                        const num = parseFloat(value.replace(",", ".")) || 0;
                        setBoleto({
                          ...boleto,
                          RegrasJuros: { ...boleto.RegrasJuros!, value: num },
                        });
                      }}
                    />
                    <FormErrorMessage>{getRegraJurosErro()}</FormErrorMessage>
                    <FormHelperText>
                      Valor máximo: {fmtBRL(tetoJurosDiaFixo)}/dia (0,033%/dia da
                      mensalidade).
                    </FormHelperText>
                  </FormControl>
                )}
              </GridItem>

              <GridItem>
                <FormControl>
                  <FormLabel>Regra de Multa</FormLabel>
                  <Select
                    value={boleto.RegrasMulta.mode}
                    onChange={(e) =>
                      setBoleto({
                        ...boleto,
                        RegrasMulta: {
                          ...boleto.RegrasMulta,
                          mode: e.target.value,
                          value: 0,
                          start_at: boleto.vencimento,
                        },
                      })
                    }
                  >
                    {OPTIONS_MULTA.map((item) => (
                      <option key={item.key} value={item.value}>
                        {item.text}
                      </option>
                    ))}
                  </Select>
                </FormControl>

                {boleto.RegrasMulta.mode === "VALOR_FIXO" && (
                  <FormControl mt={4} isInvalid={!!getRegraMultaErro()}>
                    <FormLabel>Valor Fixo (R$)</FormLabel>
                    <NumericFormat
                      decimalSeparator=","
                      decimalScale={2}
                      customInput={Input}
                      value={boleto.RegrasMulta.value}
                      placeholder="0,00"
                      onValueChange={({ value }) => {
                        const num = parseFloat(value.replace(",", ".")) || 0;
                        setBoleto({
                          ...boleto,
                          RegrasMulta: { ...boleto.RegrasMulta, value: num },
                        });
                      }}
                    />
                    <FormErrorMessage>{getRegraMultaErro()}</FormErrorMessage>
                    <FormHelperText>
                      Valor máximo: {fmtBRL(tetoMultaFixa)} (2% da mensalidade).
                    </FormHelperText>
                  </FormControl>
                )}

                {boleto.RegrasMulta.mode === "PERCENTUAL" && (
                  <FormControl mt={4} isInvalid={!!getRegraMultaErro()}>
                    <FormLabel>Percentual (%)</FormLabel>
                    <NumericFormat
                      decimalSeparator=","
                      decimalScale={2}
                      customInput={Input}
                      value={boleto.RegrasMulta.value}
                      placeholder="0,00"
                      onValueChange={({ value }) => {
                        const num = parseFloat(value.replace(",", ".")) || 0;
                        setBoleto({
                          ...boleto,
                          RegrasMulta: { ...boleto.RegrasMulta, value: num },
                        });
                      }}
                    />
                    <FormErrorMessage>{getRegraMultaErro()}</FormErrorMessage>
                    <FormHelperText>Máx. 2% do valor do boleto.</FormHelperText>
                  </FormControl>
                )}
              </GridItem>
              </Grid>
            </Box>

            {/* Bloco 2 — Desconto */}
            <Box
              borderWidth="1px"
              borderColor="gray.200"
              borderRadius="md"
              p={4}
              mb={4}
            >
              <Text fontWeight="bold" fontSize="md" mb={2}>
                Desconto por Antecipação
              </Text>
              <Alert
                status="info"
                variant="left-accent"
                borderRadius="md"
                mb={4}
                alignItems="flex-start"
              >
                <AlertIcon />
                <Box fontSize="sm">
                  <Text>
                    Valor em R$ <b>abatido</b> de cada parcela se paga com
                    antecedência. A data-limite é calculada por parcela
                    (vencimento − dias informados).
                  </Text>
                </Box>
              </Alert>
              <Grid
                templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                gap={4}
              >
                <GridItem>
                  <FormControl>
                    <FormLabel>Tipo de desconto</FormLabel>
                    <Select
                      value={boleto.RegrasDesconto.mode}
                      onChange={(e) =>
                        setBoleto({
                          ...boleto,
                          RegrasDesconto: {
                            ...boleto.RegrasDesconto,
                            mode: e.target.value,
                            value: 0,
                            diasAntesVencimento: 0,
                          },
                          descontoNaPrimeiraParcela:
                            e.target.value === "SEM_DESCONTO"
                              ? false
                              : boleto.descontoNaPrimeiraParcela,
                        })
                      }
                    >
                      {OPTIONS_DESCONTO.map((item) => (
                        <option key={item.key} value={item.value}>
                          {item.text}
                        </option>
                      ))}
                    </Select>
                  </FormControl>

                  {boleto.RegrasDesconto.mode !== "SEM_DESCONTO" && (
                    <FormControl mt={4} isInvalid={!!getRegraDescontoErro()}>
                      <FormLabel>Valor do desconto (R$)</FormLabel>
                      <NumericFormat
                        decimalSeparator=","
                        decimalScale={2}
                        customInput={Input}
                        value={boleto.RegrasDesconto.value}
                        placeholder="0,00"
                        onValueChange={({ value }) => {
                          const num = parseFloat(value.replace(",", ".")) || 0;
                          setBoleto({
                            ...boleto,
                            RegrasDesconto: {
                              ...boleto.RegrasDesconto,
                              value: num,
                            },
                          });
                        }}
                      />
                      <FormErrorMessage>
                        {getRegraDescontoErro()}
                      </FormErrorMessage>
                      <FormHelperText>
                        Abatido de cada parcela (menor que o valor da parcela).
                      </FormHelperText>
                    </FormControl>
                  )}
                </GridItem>

                {boleto.RegrasDesconto.mode !== "SEM_DESCONTO" && (
                  <GridItem>
                    <FormControl isInvalid={!!getRegraDescontoErro()}>
                      <FormLabel>Dias antes do vencimento</FormLabel>
                      <NumberInput
                        min={0}
                        value={boleto.RegrasDesconto.diasAntesVencimento}
                        onChange={(_, valueNumber) =>
                          setBoleto({
                            ...boleto,
                            RegrasDesconto: {
                              ...boleto.RegrasDesconto,
                              diasAntesVencimento: Number.isNaN(valueNumber)
                                ? 0
                                : valueNumber,
                            },
                          })
                        }
                      >
                        <NumberInputField />
                        <NumberInputStepper>
                          <NumberIncrementStepper />
                          <NumberDecrementStepper />
                        </NumberInputStepper>
                      </NumberInput>
                      <FormHelperText>
                        Válido se pago até N dias antes do vencimento de cada
                        parcela (0 = até o vencimento).
                      </FormHelperText>
                    </FormControl>
                  </GridItem>
                )}
              </Grid>

              {boleto.RegrasDesconto.mode !== "SEM_DESCONTO" && (
                <Checkbox
                  mt={4}
                  isChecked={boleto.descontoNaPrimeiraParcela}
                  onChange={(e) =>
                    setBoleto({
                      ...boleto,
                      descontoNaPrimeiraParcela: e.target.checked,
                    })
                  }
                >
                  Aplicar desconto também na 1ª parcela (entrada)
                </Checkbox>
              )}
            </Box>
          </>
        )}
      </Box>

      <Box mb={8}>
        <TitleFlex item="Informações do Pagador" />
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
          gap={4}
          my={4}
        >
          <GridItem>
            <FormControl isRequired isInvalid={validation}>
              <FormLabel>CPF/CNPJ do Pagador</FormLabel>
              <PatternFormat
                customInput={Input}
                format={
                  unMask(boleto.cpfPagador).length > 11
                    ? "##.###.###/####-##"
                    : "###.###.###-###"
                }
                placeholder="Digite o CPF ou CNPJ"
                value={boleto.cpfPagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, cpfPagador: e.target.value })
                }
                onBlur={(e) => validateCPForCNPJ(e.target.value)}
              />
              {validation && (
                <FormHelperText color={colors.REDMONEDD}>
                  Digite um CPF ou CNPJ válido!
                </FormHelperText>
              )}
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Nome do Pagador</FormLabel>
              <Input
                placeholder="Digite o nome"
                value={boleto.nomePagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, nomePagador: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>E-mail</FormLabel>
              <Input
                type="email"
                placeholder="E-mail do Pagador"
                value={boleto.emailPagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, emailPagador: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Número do Pagador</FormLabel>
              <PatternFormat
                customInput={Input}
                format="(##) #####-####"
                placeholder="(51) 99999-9999"
                value={boleto.numeroPagador}
                onChange={(e) =>
                  setBoleto({ ...boleto, numeroPagador: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
        </Grid>

        <TitleFlex item="Opções de Notificação" />
        <FormControl
          isRequired
          isInvalid={
            !boleto.enviarEmail && !boleto.enviarSMS && !boleto.enviarWhats
          }
        >
          <Grid
            templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }}
            gap={4}
            my={4}
          >
            <GridItem>
              <FormControl>
                <FormLabel display="flex" alignItems="center">
                  <FaEnvelope
                    color={colors.BLUEMONEDDBUTTONS}
                    size="16px"
                    style={{ marginRight: "8px" }}
                  />
                  Enviar Notificação por E-mail
                </FormLabel>
                <Switch
                  isChecked={true}
                  isReadOnly
                />
              </FormControl>
            </GridItem>
            {/* <GridItem>
              <FormControl>
                <FormLabel display="flex" alignItems="center">
                  <FaSms
                    color={colors.BLUEMONEDDBUTTONS}
                    size="16px"
                    style={{ marginRight: "8px" }}
                  />
                  Enviar Notificação por SMS
                </FormLabel>
                <Switch
                  isChecked={boleto.enviarSMS}
                  onChange={(e) =>
                    setBoleto({ ...boleto, enviarSMS: e.target.checked })
                  }
                />
              </FormControl>
            </GridItem>
            <GridItem>
              <FormControl>
                <FormLabel display="flex" alignItems="center">
                  <FaWhatsapp
                    color={colors.BLUEMONEDDBUTTONS}
                    size="16px"
                    style={{ marginRight: "8px" }}
                  />
                  Enviar Notificação por WhatsApp
                </FormLabel>
                <Switch
                  isChecked={boleto.enviarWhats}
                  onChange={(e) =>
                    setBoleto({ ...boleto, enviarWhats: e.target.checked })
                  }
                />
              </FormControl>
            </GridItem> */}
          </Grid>
          <FormHelperText color={colors.REDMONEDD}>
            {!boleto.enviarEmail && !boleto.enviarSMS && !boleto.enviarWhats
              ? "Selecione pelo menos uma opção de notificação."
              : ""}
          </FormHelperText>
        </FormControl>
      </Box>

      <Box mb={8}>
        <TitleFlex item="Endereço de Cobrança" />
        <Grid
          templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
          gap={4}
          my={4}
        >
          <GridItem>
            <FormControl isRequired>
              <FormLabel>CEP</FormLabel>
              <PatternFormat
                customInput={Input}
                format="#####-###"
                value={boleto.enderCEP}
                onChange={(e) => {
                  setBoleto({ ...boleto, enderCEP: e.target.value });
                  addressCep(e.target.value);
                }}
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Endereço</FormLabel>
              <Input
                value={boleto.enderRua}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderRua: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Número</FormLabel>
              <Input
                value={boleto.enderNumero}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderNumero: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>Complemento</FormLabel>
              <Input
                value={boleto.enderComplemento}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderComplemento: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl>
              <FormLabel>Referência</FormLabel>
              <Input
                value={boleto.enderReferencia}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderReferencia: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Bairro</FormLabel>
              <Input
                value={boleto.enderBairro}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderBairro: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Cidade</FormLabel>
              <Input
                value={boleto.enderCidade}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderCidade: e.target.value })
                }
              />
            </FormControl>
          </GridItem>
          <GridItem>
            <FormControl isRequired>
              <FormLabel>Estado</FormLabel>
              <Select
                value={boleto.enderEstado}
                onChange={(e) =>
                  setBoleto({ ...boleto, enderEstado: e.target.value })
                }
              >
                {stateOptions.map((item, index) => (
                  <option value={item.value} key={index}>
                    {item.text}
                  </option>
                ))}
              </Select>
            </FormControl>
          </GridItem>
        </Grid>
      </Box>

      <Flex
        justifyContent="space-between"
        w="100%"
        mt={6}
        flexDir={{ base: "column", md: "row" }}
        gap={4}
      >
        <Button
          bgColor={colors.GRAY_DARK}
          color={colors.WHITE}
          w={{ base: "100%", md: "200px" }}
          onClick={() => goToVendaBolePixListagem(navigate)}
        >
          Cancelar
        </Button>
        <Button
          bgColor={colors.BLUEMONEDDBUTTONS}
          color={colors.WHITE}
          isDisabled={!isFormValid() || loadingState}
          w={{ base: "100%", md: "300px" }}
          onClick={async () => {
            setLoadingState(true);
            await onSubmit();
          }}
        >
          {loadingState ? (
            <Spinner size="sm" />
          ) : (
            `Criar ${numBoletos} Boleto${numBoletos > 1 ? "s" : ""}`
          )}
        </Button>
      </Flex>

      <ModalRevisãoVendaBoletoLote
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        batchObjects={batchObjects}
        setBatchObjects={setBatchObjects}
        setBatchResponses={setBatchResponses}
        setBoleto={setBoleto}
        initialValues={initialValues}
        setLoadingState={setLoadingState}
        numBoletos={numBoletos}
        cliente={cliente}
        idLogin={idLogin}
        handleBatchCreation={handleBatchCreation}
      />
    </Flex>
  );
}
